import * as Sentry from "@sentry/react";

import { Paragraph, StyledAlert } from "App/global-style";
import { Form, FormikProps } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  VETERAN_AUTO_DETAILS_STEP,
  VETERAN_AUTO_INTRO_STEP,
  VETERAN_AUTO_PRICE_STEP,
} from "..";
import {
  VeteranAutoPriceRequestInfo,
  VeteranAutoProductDetailsFormValues,
  VeteranAutoProductIntroFormValues,
  VeteranAutoProductPriceFormValues,
  VeteranAutoTexts,
} from "../veteranAutoInterface";

import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile";
import { AlertType } from "Components/Form/Alert";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { useAddProductToCartHandler } from "Store/addProductToCart/hooks";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { VeteranAutoCoverage } from "Store/interfaces/products/coverage";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { useErrorTexts } from "Store/texts/hooks";
import { TextLanguage } from "Store/texts/interfaces";
import { getDebounce } from "Utils/debounce";
import { mapValidationErrors } from "Utils/formUtils";
import { isNull } from "Utils/functions";
import React from "react";
import { getIsMemberOfVeteranClubSchema } from "../ProductDetails/veteranAutoProductDetailsValidation";
import { mapFormToPriceRequest } from "../veteranAutoUtils";
import { FormElements } from "./FormElements";
import { VeteranAutoCoverages } from "./VeteranAutoCoverages";
import { validationSchema } from "./veteranAutoProductPriceValidation";

const debounce = getDebounce(500);

interface Props {
  readonly sendPriceRequest: (
    payload: VeteranAutoPriceRequestInfo,
    stepForwardOnSuccess?: boolean,
  ) => void;
  readonly texts: VeteranAutoTexts;
  readonly lang: TextLanguage;
}

export const VeteranAutoProductPrice = (props: Props): JSX.Element => {
  const dispatch = useDispatch();
  const { sendPriceRequest, texts, lang } = props;
  const addProductToCart = useAddProductToCartHandler(
    VETERAN_AUTO_PRICE_STEP.id,
  );

  const introFormValues = useFormState(
    VETERAN_AUTO_INTRO_STEP.id,
  ) as VeteranAutoProductIntroFormValues;
  const detailsFormValues = useFormState(
    VETERAN_AUTO_DETAILS_STEP.id,
  ) as VeteranAutoProductDetailsFormValues;
  const priceFormValues = useFormState(
    VETERAN_AUTO_PRICE_STEP.id,
  ) as VeteranAutoProductPriceFormValues;

  const initialFormState = useFormState<VeteranAutoProductPriceFormValues>(
    VETERAN_AUTO_PRICE_STEP.id,
    {
      isMemberOfVeteranClub:
        priceFormValues.isMemberOfVeteranClub ?? BooleanFormType.FALSE,
      coverage: VeteranAutoCoverage.VETERAN_AUTO_FULL_OWN_DAMAGE,
    },
  );

  const [{ callUsError, callUsAlertTitle }] = useErrorTexts();
  const priceCalculation = useSelector(
    (state: ReduxState) => state.priceCalculation.priceCalculationResult,
  );
  const { addProductToCart: submitState } = useSelector(
    (state: ReduxState) => state,
  );

  const handleSubmit = (
    formValues: VeteranAutoProductPriceFormValues,
  ): void => {
    const productToAdd = {
      coverage: formValues.coverage,
      productType: INSURANCE_NAME_VALUE.VETERAN_AUTO,
    };
    const tfaAmount =
      priceCalculation.current?.[formValues.coverage]?.tfaAmount;
    addProductToCart({ ...formValues, tfaAmount }, [productToAdd]);
  };

  const updatePrices = (isMemberOfVeteranClub: BooleanFormType): void => {
    const formValues = {
      ...priceFormValues,
      isMemberOfVeteranClub,
    };

    dispatch(
      updatePriceCalculationFormState({
        stepId: VETERAN_AUTO_DETAILS_STEP.id,
        formState: formValues,
      }),
    );

    const isValid = getIsMemberOfVeteranClubSchema().isValidSync(formValues);

    if (!isValid) {
      return;
    }

    debounce(
      sendPriceRequest,
      mapFormToPriceRequest(introFormValues, detailsFormValues, formValues),
      false,
    );
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductPrice - ${INSURANCE_NAME_VALUE.VETERAN_AUTO}`,
        );
      }}
    >
      <ProductPriceTile
        header={texts.tile.productPrice.heading[lang]}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
        validationSchema={validationSchema()}
      >
        {(formProps: FormikProps<VeteranAutoProductPriceFormValues>) => (
          <Form>
            <Paragraph>{texts.tile.productPrice.ingress[lang]}</Paragraph>
            <FormElements
              formProps={formProps}
              updatePrices={updatePrices}
              texts={texts}
              lang={lang}
            />
            <StyledAlert
              title={texts.tile.productPrice.deductibleInfo.description[lang]}
              marginTop="0px"
              fluid
            />
            <VeteranAutoCoverages texts={texts} lang={lang} />

            {!isNull(submitState.error) && (
              <StyledAlert
                type="error"
                title={callUsAlertTitle[lang]}
                description={callUsError[lang]}
                fluid
                alertType={AlertType.SYSTEM_ERROR_ALERT}
              />
            )}

            <ErrorsSummedUp
              errors={mapValidationErrors(
                formProps.errors,
                texts.details,
                lang,
              )}
              isVisible
            />

            <ScrollToFirstError />

            <NavigationButtons isUpsell isLoading={submitState.isLoading} />
          </Form>
        )}
      </ProductPriceTile>
    </Sentry.ErrorBoundary>
  );
};
